import type { ReactNode } from 'react';

import { Dialog as DialogPrimitive, VisuallyHidden } from 'radix-ui';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/ui/cn';
import { IconButton } from '@/shared/ui/IconButton';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const WIDTH: Record<DialogSize, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[680px]',
  xl: 'max-w-[920px]',
};

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Titre lu et affiché. Masqué à l'œil (mais lu) avec `hideTitle`. */
  title: ReactNode;
  hideTitle?: boolean;
  description?: ReactNode;
  /** Barre d'actions, toujours visible sous le contenu qui défile. */
  footer?: ReactNode;
  size?: DialogSize;
  /** Retire la croix (une décision qui doit se prendre par les boutons). */
  hideClose?: boolean;
  /** Le contenu touche les bords (liste pleine largeur). */
  bleed?: boolean;
  className?: string;
  children?: ReactNode;
};

/**
 * Boîte de dialogue centrée : la fenêtre qui demande une décision courte, un
 * motif, un montant. Le contenu défile quand il dépasse ; la barre d'actions
 * reste en place. Échap, clic sur le voile et croix la ferment ; le focus est
 * retenu dedans et revient au bouton qui l'a ouverte.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  hideTitle = false,
  description,
  footer,
  size = 'sm',
  hideClose = false,
  bleed = false,
  className,
  children,
}: DialogProps) {
  const { t } = useTranslation();

  const titleNode = (
    <DialogPrimitive.Title className="text-title3 text-fg">{title}</DialogPrimitive.Title>
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in bg-backdrop" />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex max-h-[min(88dvh,760px)] w-[calc(100%-32px)] -translate-x-1/2 -translate-y-1/2 flex-col',
            'animate-scale-in overflow-hidden rounded-lg bg-raised shadow-popover outline-none',
            WIDTH[size],
            className,
          )}
        >
          <div className={cn('flex items-start gap-3 px-5 pt-5', hideTitle && 'pt-3')}>
            <div className="min-w-0 flex-1">
              {hideTitle ? <VisuallyHidden.Root>{titleNode}</VisuallyHidden.Root> : titleNode}
              {description ? (
                <DialogPrimitive.Description className="mt-1.5 text-subtle text-fg-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <VisuallyHidden.Root>
                  <DialogPrimitive.Description>{typeof title === 'string' ? title : ''}</DialogPrimitive.Description>
                </VisuallyHidden.Root>
              )}
            </div>
            {hideClose ? null : (
              <DialogPrimitive.Close asChild>
                <IconButton name="close" label={t('action.close')} size="sm" className="-mt-1 -mr-2" />
              </DialogPrimitive.Close>
            )}
          </div>
          <div className={cn('min-h-0 flex-1 overflow-y-auto', bleed ? 'py-3' : 'px-5 py-4')}>
            {children}
          </div>
          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-line px-5 py-3">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
